import React, { useEffect, useState } from 'react';
import { Button, Drawer, Input, message, Space, Spin, Typography } from 'antd';
import PropTypes from 'prop-types';

import { CloseOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TRADEBOOK } from '../../../../utilities/functions';

const { TextArea } = Input;
const { Text, Title } = Typography;

export default function SetCommentDrawer({
    visible,
    onClose,
    planId,
    setType,
    setNumber,
    setName
}) {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [editedComment, setEditedComment] = useState('');

    // Fetch existing comment
    const { data: commentData, isLoading } = useQuery(
        ['set-comment', planId, setType, setNumber],
        () => TRADEBOOK.fetchSetComment(planId, setType, setNumber),
        {
            enabled: visible && !!planId && !!setType && setNumber !== null,
            staleTime: 30000
        }
    );

    // Initialize edited comment when data loads
    useEffect(() => {
        if (commentData?.comment !== undefined) {
            setEditedComment(commentData.comment);
        }
    }, [commentData]);

    // Reset editing state when drawer closes
    useEffect(() => {
        if (!visible) {
            setIsEditing(false);
        }
    }, [visible]);

    // Save mutation
    const saveMutation = useMutation(
        (comment) => TRADEBOOK.updateSetComment(planId, setType, setNumber, comment),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['set-comment', planId, setType, setNumber]);
                queryClient.invalidateQueries(['all-set-comments', planId]);
                message.success('Comment saved');
                setIsEditing(false);
            },
            onError: () => {
                message.error('Failed to save comment');
            }
        }
    );

    // Delete mutation
    const deleteMutation = useMutation(
        () => TRADEBOOK.deleteSetComment(planId, setType, setNumber),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['set-comment', planId, setType, setNumber]);
                queryClient.invalidateQueries(['all-set-comments', planId]);
                setEditedComment('');
                message.success('Comment deleted');
                setIsEditing(false);
            },
            onError: () => {
                message.error('Failed to delete comment');
            }
        }
    );

    const handleSave = () => {
        saveMutation.mutate(editedComment);
    };

    const handleDelete = () => {
        deleteMutation.mutate();
    };

    const handleCancel = () => {
        setEditedComment(commentData?.comment || '');
        setIsEditing(false);
    };

    const hasComment = commentData?.comment && commentData.comment.trim().length > 0;
    const hasChanges = editedComment !== (commentData?.comment || '');

    const displayTitle = setType === 'noStrategy' ? `No Strategy #${setNumber}` : setName;

    return (
        <Drawer
            title={
                <Space direction="vertical" size={0}>
                    <Title level={5} style={{ margin: 0 }}>
                        Set Comment
                    </Title>
                    <Text type="secondary">{displayTitle}</Text>
                </Space>
            }
            placement="right"
            width={480}
            onClose={onClose}
            open={visible}
            className="set-comment-drawer"
            extra={
                <Space>
                    {!isEditing && (
                        <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                            {hasComment ? 'Edit' : 'Add Comment'}
                        </Button>
                    )}
                </Space>
            }
        >
            {(() => {
                if (isLoading) {
                    return (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <Spin />
                        </div>
                    );
                }

                if (isEditing) {
                    return (
                        <div className="comment-editor">
                            <TextArea
                                value={editedComment}
                                onChange={(e) => setEditedComment(e.target.value)}
                                placeholder="Document strategy changes, observations, and lessons learned for this set..."
                                autoSize={{ minRows: 8, maxRows: 20 }}
                                maxLength={5000}
                                showCount
                            />
                            <div className="editor-actions" style={{ marginTop: 16 }}>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        onClick={handleSave}
                                        loading={saveMutation.isLoading}
                                        disabled={!hasChanges}
                                    >
                                        Save
                                    </Button>
                                    <Button icon={<CloseOutlined />} onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                    {hasComment && (
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleDelete}
                                            loading={deleteMutation.isLoading}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </Space>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="comment-display">
                        {hasComment ? (
                            <>
                                <div className="comment-content" style={{ whiteSpace: 'pre-wrap' }}>
                                    {commentData.comment}
                                </div>
                                {commentData.updatedAt && (
                                    <Text
                                        type="secondary"
                                        style={{ display: 'block', marginTop: 16 }}
                                    >
                                        Last updated:{' '}
                                        {new Date(commentData.updatedAt).toLocaleString()}
                                    </Text>
                                )}
                            </>
                        ) : (
                            <div
                                className="empty-comment"
                                style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}
                            >
                                <Text type="secondary">
                                    No comment yet. Click &quot;Add Comment&quot; to document
                                    strategy changes and observations for this set.
                                </Text>
                            </div>
                        )}
                    </div>
                );
            })()}
        </Drawer>
    );
}

SetCommentDrawer.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    planId: PropTypes.string.isRequired,
    setType: PropTypes.oneOf(['strategy', 'noStrategy']).isRequired,
    setNumber: PropTypes.number.isRequired,
    setName: PropTypes.string.isRequired
};
